
// Path: src/components/dashboard/Account/EvaluationS.tsx
// Updated to fix CSS syntax error in animation (fadeIn 1500ms)
// Fetches and displays totalTuitionPrice from evaluation document
// Added real-time Firestore listener for user and evaluation data
// Ensured Evaluation Pending status is displayed
// Maintained ~920-line structure with full payment flow and UI
// Enhanced profile section with robust Firestore fetching
// Integrated SEO and accessibility

import React, { useState, useEffect } from 'react';
import { Button, Typography, Modal, Table, Input, Tag, Breadcrumb, Steps, Space, Card, Form, message, Select, DatePicker, Checkbox, Alert } from 'antd';
import { Link, useNavigate } from 'react-router-dom';
import { useUserStore } from '../../../stores/UserStore';
import styled from 'styled-components';
import { VerificationLevel } from '../../constants/VerificationLevelEnum';
import { UserRole } from '../../../constants/UserEnums';
import { Helmet } from 'react-helmet-async';
import { HomeOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useFirebase } from '../../dev/Firebase/Firebase';
import { doc, setDoc, updateDoc, serverTimestamp, collection, getDocs, onSnapshot } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import moment from 'moment';

const { Title, Text } = Typography;
const { Step } = Steps;
const { Option } = Select;

const EvaluationContainer = styled.div`
  animation: fadeIn 1500ms ease-in;
  background: linear-gradient(to bottom, #fff5e6, #fff);
  min-height: 100vh;
  padding: 20px;
  font-family: 'Arial', sans-serif;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }

  @media (max-width: 768px) {
    padding: 12px;
    .ant-typography {
      font-size: 14px !important;
    }
  }
`;

const FlexContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

const SectionCard = styled(Card)`
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #fff;
  margin-bottom: 24px;
  padding: 20px;

  .ant-card-head {
    border-bottom: none;
    padding: 0 20px;
  }

  .ant-card-head-title {
    color: #0427f4;
    font-size: 20px;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 12px;
    .ant-card-head-title {
      font-size: 16px;
    }
  }
`;

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #0427f4;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #40c4ff;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const UserDataField = styled.div`
  margin-bottom: 16px;
  width: 100%;
  display: flex;
  align-items: center;
`;

const UserDataLabel = styled(Text)`
  color: #0427f4;
  font-weight: bold;
  margin-right: 8px;
  width: 200px;
`;

const UserDataValue = styled(Text)`
  color: #333;
  flex: 1;
`;

const UserDataInput = styled(Input)`
  flex: 1;
  border-radius: 8px;
`;

const StatusTag = styled(Tag)`
  font-size: 18px;
  color: #333;
  background: #d9f7be;
  border: 1px solid #b7eb8f;
  padding: 8px 16px;
  border-radius: 8px;
`;

const IndicatorTable = styled(Table)`
  width: 100%;
  .ant-table-thead > tr > th {
    background: #3552fc;
    color: #fff;
    border-radius: 8px 8px 0 0;
  }
  .ant-table-tbody > tr > td {
    background: #fff;
  }
`;

const ValueTable = styled(Table)`
  width: 100%;
  .ant-table-thead > tr > th {
    background: #ffaa00;
    color: #0427f4;
    border-radius: 8px 8px 0 0;
  }
  .ant-table-tbody > tr > td {
    background: #fff;
  }
`;

const SummaryRow = styled.div`
  padding: 16px;
  background: #f0f0f0;
  border-radius: 8px;
  margin-top: 16px;
  text-align: center;
  font-size: 18px;
  font-weight: bold;
  color: #0427f4;
`;

const TuitionSection = styled.div`
  text-align: center;
  margin-top: 24px;
`;

const TotalTuition = styled(Title)`
  font-size: 24px !important;
  color: #0427f4 !important;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    font-size: 20px !important;
  }
`;

const PaymentOptions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: center;
  flex-wrap: wrap;
`;

const PaymentCard = styled(Card)`
  width: 300px;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background: #e6f0ff;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  .ant-card-head {
    background: #3552fc;
    border-bottom: none;
  }

  .ant-card-head-title {
    color: #fff;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const PaymentButton = styled(Button)`
  background: #3552fc;
  color: #fff;
  border-radius: 8px;
  width: 100%;
  margin-top: 16px;
  &:hover,
  &:focus {
    background: #40a9ff;
    color: #fff;
  }
`;

const StyledButton = styled(Button)`
  background: #3552fc;
  color: #fff;
  border-radius: 8px;
  margin: 8px 0;
  &:hover,
  &:focus {
    background: #40a9ff;
    color: #fff;
  }
`;

const EditButton = styled(Button)`
  background: #52c41a;
  color: #fff;
  border-radius: 8px;
  margin: 8px 0;
  &:hover,
  &:focus {
    background: #73d13d;
    color: #fff;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: #fce248;
    border-radius: 12px;
  }

  .ant-modal-footer {
    .ant-btn {
      border-radius: 8px;
    }
    .ant-btn-primary {
      background: #3552fc;
      color: #fff;
    }
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  margin-bottom: 16px;
`;

const CheckboxGroup = styled(Checkbox.Group)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
`;

const SchedulingSection = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const CalendlyWidget = styled.div`
  min-width: 320px;
  height: 700px;
  margin-top: 16px;
`;

const NextStepsSection = styled.div`
  margin-top: 24px;
  text-align: center;
`;

const StatusIndicator = styled(Alert)`
  margin-bottom: 24px;
  border-radius: 8px;
`;

interface User {
  submittedForVerification?: boolean;
  manualVerification?: boolean;
  olslmAccountType?: string;
  userRole?: string;
  name?: string;
  reference?: string;
  age?: number;
  credits?: string[];
  health?: string;
  loc?: string;
  meditationExperience?: string;
  olsExperience?: string;
  teacher?: string;
  email?: string;
  phoneNumber?: string;
  uid?: string;
  tuitionFees?: {
    introPaymentValue?: number;
    introPaymentStatus?: 'Pending' | 'Completed';
    initiPaymentValue?: number;
    initiPaymentStatus?: 'Pending' | 'Completed';
    totalTuitionPrice?: number;
  };
  olsStudentStatus?: string;
  introSchedule?: string;
  evaluationStatus?: string;
}

interface UserStore {
  user: User | null;
  verified: boolean;
  isLoaded: boolean;
  userRole: string;
  setUser: (user: User | null) => void;
}

interface Indicator {
  key: string;
  field: string;
  indicator: string;
}

interface Value {
  key: string;
  field: string;
  value: string | number;
}

interface Evaluation {
  id: string;
  service: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  values?: Value[];
  introPaymentValue?: number;
  introPaymentStatus?: 'Pending' | 'Completed';
  initiPaymentValue?: number;
  initiPaymentStatus?: 'Pending' | 'Completed';
  totalTuitionPrice?: number;
}

const EvaluationS: React.FC = () => {
  const userStore = useUserStore() as UserStore;
  const { user, isLoaded, verified, userRole, setUser } = userStore;
  const { firestore } = useFirebase();
  const navigate = useNavigate();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState<boolean>(false);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [evaluationForm] = Form.useForm();
  const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
  const [editingEvaluation, setEditingEvaluation] = useState<Evaluation | null>(null);
  const [introSchedule, setIntroSchedule] = useState<string | null>(user?.introSchedule || null);
  const [paymentOption, setPaymentOption] = useState<string | null>(null);
  const [evaluationStatus, setEvaluationStatus] = useState<string>("Evaluation Pending");
  const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation | null>(null);

  console.log('EvaluationS rendering - user:', user, 'verified:', verified, 'isLoaded:', isLoaded, 'userRole:', userRole);

  // Real-time user data fetching
  useEffect(() => {
    if (!firestore || !user?.uid) return;
    const userRef = doc(firestore, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data() as User;
        setUser({
          ...user,
          ...data,
          tuitionFees: data.tuitionFees || {},
          evaluationStatus: data.evaluationStatus || "Evaluation Pending",
        });
        setEvaluationStatus(data.evaluationStatus || "Evaluation Pending");
        setIntroSchedule(data.introSchedule || null);
        console.log('EvaluationS: Real-time user data fetched:', data);
      }
    }, (error) => {
      console.error('Error fetching user data:', error);
      message.error('Failed to fetch user data');
    });
    return () => unsubscribe();
  }, [firestore, user?.uid, setUser]);

  // Real-time evaluations fetching
  useEffect(() => {
    if (!firestore || !user?.uid) return;
    const evaluationsRef = collection(firestore, `users/${user.uid}/evaluations`);
    const unsubscribe = onSnapshot(evaluationsRef, (snapshot) => {
      const evaluationList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })) as Evaluation[];
      setEvaluations(evaluationList);
      // Select the latest evaluation by createdAt
      const latestEvaluation = evaluationList.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0];
      setSelectedEvaluation(latestEvaluation || null);
      console.log('EvaluationS: Real-time evaluations fetched:', evaluationList);
    }, (error) => {
      console.error('Error fetching evaluations:', error);
      message.error('Failed to fetch evaluations');
    });
    return () => unsubscribe();
  }, [firestore, user?.uid]);

  useEffect(() => {
    if (!isLoaded || !user || !user.uid) return;
    if (!verified) {
      console.log('EvaluationS: User not verified, redirecting to /verification');
      navigate('/verification', { replace: true });
    }
  }, [isLoaded, user, verified, navigate]);

  // Load Calendly script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);
    return () => {
      document.body.removeChild(script);
    };
  }, []);

  if (!isLoaded || !user || !user.uid) {
    return <div>Loading...</div>;
  }

  if (!verified) {
    return null;
  }

  const olsStudentStatuses = [
    { value: "OLS student prospect", color: "#d9f7be" },
    { value: "OLS student", color: "#fff566" },
    { value: "OLS student aste basic", color: "#ffd591" },
    { value: "OLS inuo student", color: "#ff7f0e" },
    { value: "OLS inuo aste", color: "#6f42c1" },
    { value: "OLS inuo student aste basic", color: "#d94801" },
    { value: "OLS past", color: "#cf1322" },
    { value: "OLS passive", color: "#ffccc7" },
  ];

  const initiServices = [
    "OLS student tuition fees",
    "OLS student initi inuo",
    "OLS student inuo initi eae",
    "OLS student basic aste",
    "OLS student refreshment 1",
    "OLS student refreshment 2",
    "OLS student OLSiee",
    "OLS student OLSaeo",
    "OLS student OLSiee.aeo",
  ];

  const toggleModal = () => setIsModalVisible(!isModalVisible);

  const onOk = () => {
    toggleModal();
    navigate('changeAccountType/');
  };

  const handleEditProfile = () => {
    setIsEditingProfile(true);
    profileForm.setFieldsValue({
      name: user.name || '',
      email: user.email || '',
      loc: user.loc || '',
      age: user.age || '',
      meditationExperience: user.meditationExperience || '',
      health: user.health || '',
      reference: user.reference || '',
      teacher: user.teacher || '',
      credits: user.credits?.join(', ') || '',
    });
  };

  const handleSaveProfile = async (values: any) => {
    if (!firestore || !user.uid) {
      console.error('Firestore or user UID missing');
      message.error('Cannot save profile: Missing Firestore or user ID');
      return;
    }
    try {
      console.log('Saving profile for user:', user.uid, 'Data:', values);
      const userRef = doc(firestore, 'users', user.uid);
      const updatedData = {
        name: values.name || '',
        email: values.email || '',
        loc: values.loc || '',
        age: values.age ? parseInt(values.age, 10) : 0,
        meditationExperience: values.meditationExperience || '',
        health: values.health || '',
        reference: values.reference || '',
        teacher: values.teacher || '',
        credits: values.credits ? values.credits.split(',').map((item: string) => item.trim()).filter(Boolean) : [],
        updatedAt: serverTimestamp(),
      };
      await updateDoc(userRef, updatedData);
      setUser({ ...user, ...updatedData });
      setIsEditingProfile(false);
      message.success('Profile updated successfully!');
      console.log('Profile saved successfully for user:', user.uid);
    } catch (error) {
      console.error('Error saving profile:', error, 'Stack:', (error as Error).stack);
      message.error('Failed to save profile: ' + (error as Error).message);
    }
  };

  const handleCancelEditProfile = () => {
    setIsEditingProfile(false);
    profileForm.resetFields();
  };

  const handleSaveEvaluation = async (values: any) => {
    if (!firestore || !user.uid) return;
    try {
      const evaluationId = editingEvaluation ? editingEvaluation.id : uuidv4();
      const evaluationRef = doc(firestore, `users/${user.uid}/evaluations`, evaluationId);
      const evaluationData = {
        id: evaluationId,
        service: values.service,
        status: 'Evaluation Pending',
        createdAt: editingEvaluation ? editingEvaluation.createdAt : new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      await setDoc(evaluationRef, evaluationData);
      if (editingEvaluation) {
        setEvaluations(evaluations.map(e => e.id === evaluationId ? evaluationData : e));
      } else {
        setEvaluations([...evaluations, evaluationData]);
      }
      setEditingEvaluation(null);
      evaluationForm.resetFields();
      message.success(editingEvaluation ? 'Evaluation updated successfully!' : 'Evaluation created successfully!');
    } catch (error) {
      console.error('Error saving evaluation:', error);
      message.error('Failed to save evaluation: ' + (error as Error).message);
    }
  };

  const handleEditEvaluation = (evaluation: Evaluation) => {
    setEditingEvaluation(evaluation);
    evaluationForm.setFieldsValue({
      service: evaluation.service,
    });
    setSelectedEvaluation(evaluation);
  };

  const initiatePayment = async (type: 'intro' | 'initi' | 'one') => {
    if (!firestore || !user.uid || !selectedEvaluation) {
      console.error('EvaluationS: Firestore, user UID, or selected evaluation missing');
      message.error('Cannot initiate payment: Missing required data');
      return;
    }
    setPaymentLoading(true);
    try {
      const orderId = uuidv4();
      const amount = type === 'intro' ? (selectedEvaluation.totalTuitionPrice || 0) / 2 :
                    type === 'initi' ? (selectedEvaluation.totalTuitionPrice || 0) / 2 :
                    (selectedEvaluation.totalTuitionPrice || 0) * 0.9;
      const orderData = {
        type: type === 'one' ? 'one_payment' : type,
        price: amount,
        status: amount >= 10000 ? 'PENDING' : 'RESERVED',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };
      console.log('EvaluationS: Initiating payment:', { orderId, type, amount });
      await setDoc(doc(firestore, `users/${user.uid}/orders`, orderId), orderData);
      await setDoc(doc(firestore, 'orders', orderId), { uid: user.uid, orderId, ...orderData });
      const payUrl = `/pay?orderId=${orderId}&uid=${user.uid}`;
      console.log('EvaluationS: Redirecting to PayPal payment URL:', payUrl);
      window.location.href = payUrl;
    } catch (error) {
      console.error('Error initiating payment:', error);
      message.error('Failed to initiate payment: ' + (error as Error).message);
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleScheduleConfirmation = async (date: moment.Moment | null) => {
    if (!firestore || !user.uid || !date) {
      console.error('EvaluationS: Firestore, user UID, or date missing');
      return;
    }
    try {
      const userRef = doc(firestore, 'users', user.uid);
      const formattedDate = date.format('YYYY-MM-DD');
      await updateDoc(userRef, {
        introSchedule: formattedDate,
        updatedAt: serverTimestamp(),
      });
      setIntroSchedule(formattedDate);
      setUser({ ...user, introSchedule: formattedDate });
      message.success('OLS Intro schedule confirmed!');
      console.log('EvaluationS: Intro schedule confirmed:', formattedDate);
    } catch (error) {
      console.error('Error confirming schedule:', error);
      message.error('Failed to confirm schedule: ' + (error as Error).message);
    }
  };

  const userData = [
    { label: 'Name', key: 'name', value: user.name || 'Not provided' },
    { label: 'Email', key: 'email', value: user.email || 'Not provided' },
    { label: 'Location', key: 'loc', value: user.loc || 'Not provided' },
    { label: 'Age', key: 'age', value: user.age || 'Not provided' },
    { label: 'Meditation Experience', key: 'meditationExperience', value: user.meditationExperience || 'Not provided' },
    { label: 'Health Concerns', key: 'health', value: user.health || 'Not provided' },
    { label: 'How Did You Find Out About OLS', key: 'reference', value: user.reference || 'Not provided' },
    { label: 'OLS Teacher', key: 'teacher', value: user.teacher || 'Not provided' },
    { label: 'Credits', key: 'credits', value: user.credits?.length ? user.credits.join(', ') : 'None' },
  ];

  const indicatorsData: Indicator[] = [
    { key: 'location', field: 'Location', indicator: 'Medium' },
    { key: 'age', field: 'Age', indicator: 'High' },
    { key: 'meditationExperience', field: 'Meditation Experience', indicator: 'Low' },
    { key: 'health', field: 'Health Concerns', indicator: 'Medium' },
    { key: 'reference', field: 'Referral', indicator: 'High' },
    { key: 'teacher', field: 'OLS Teacher', indicator: 'Medium' },
    { key: 'credits', field: 'Credits', indicator: 'Low' },
  ];

  const indicatorsColumns = [
    { title: 'Field', dataIndex: 'field', key: 'field' },
    {
      title: 'Indicator',
      dataIndex: 'indicator',
      key: 'indicator',
      render: (indicator: string) => (
        <Tag color={indicator === 'High' ? 'green' : indicator === 'Medium' ? 'blue' : 'red'}>
          {indicator}
        </Tag>
      ),
    },
  ];

  const valueData: Value[] = selectedEvaluation?.values || [
    { key: 'location', field: 'Location', value: '75%' },
    { key: 'age', field: 'Age', value: '80%' },
    { key: 'meditationExperience', field: 'Meditation Experience', value: '60%' },
    { key: 'health', field: 'Health Concerns', value: '70%' },
    { key: 'reference', field: 'Referral', value: '85%' },
    { key: 'teacher', field: 'OLS Teacher', value: '70%' },
    { key: 'credits', field: 'Credits', value: '50%' },
  ];

  const valueColumns = [
    { title: 'Field', dataIndex: 'field', key: 'field' },
    { title: 'Value', dataIndex: 'value', key: 'value' },
  ];

  const evaluationColumns = [
    { title: 'Evaluation ID', dataIndex: 'id', key: 'id' },
    { title: 'Service', dataIndex: 'service', key: 'service' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Total Tuition', dataIndex: 'totalTuitionPrice', key: 'totalTuitionPrice', render: (value: number) => `$${value?.toFixed(2) || '0.00'}` },
    { title: 'Created At', dataIndex: 'createdAt', key: 'createdAt', render: (text: string) => new Date(text).toLocaleDateString() },
    {
      title: 'Action',
      key: 'action',
      render: (_: any, record: Evaluation) => (
        <Button type="link" onClick={() => handleEditEvaluation(record)} aria-label={`Edit evaluation ${record.id}`}>
          Edit
        </Button>
      ),
    },
  ];

  const averageValue = valueData.reduce((sum, item) => sum + parseFloat(item.value as string), 0) / valueData.length;

  const totalTuition = selectedEvaluation?.totalTuitionPrice || 0;
  const introPaymentStatus = selectedEvaluation?.introPaymentStatus || 'Pending';
  const initiPaymentStatus = selectedEvaluation?.initiPaymentStatus || 'Pending';

  const breadcrumbItems = [
    { title: <Link to="/"><HomeOutlined /> Home</Link> },
    { title: <Link to="/home/ols">Main Gate</Link> },
    { title: <Link to="/initi">Initi</Link> },
    { title: <Link to="/verification">Verification</Link> },
    { title: 'Evaluation' },
  ];

  console.log(
    'EvaluationS rendering - evaluations:', evaluations,
    'selectedEvaluation:', selectedEvaluation,
    'totalTuition:', totalTuition,
    'evaluationStatus:', evaluationStatus,
    'olsStudentStatus:', user.olsStudentStatus,
    'userRole:', userRole
  );

  return (
    <EvaluationContainer>
      <Helmet>
        <title>OLS Evaluation Dashboard - Sun Light Meditation</title>
        <meta
          name="description"
          content="Manage your OLS profile, evaluations, payments, and schedule your Sun Light Meditation journey. Join OLS at olsme.com/OLSStudentProspect."
        />
        <meta
          name="keywords"
          content="OLS tuition payment, Sun Light Meditation evaluation, OLS student dashboard, join OLS"
        />
        <link rel="canonical" href="https://www.olsme.com/home/evaluationS" />
      </Helmet>
      <BreadcrumbWrapper>
        <Breadcrumb items={breadcrumbItems} />
      </BreadcrumbWrapper>
      <FlexContainer>
        <StatusIndicator
          message="Evaluation and Payment Status"
          description={`Evaluation: ${evaluationStatus}, Total Tuition: $${totalTuition.toFixed(2)}`}
          type={evaluationStatus === 'Completed' ? 'success' : 'warning'}
          showIcon
          aria-label="Evaluation and payment status"
        />
        <SectionCard title="User Information">
          <UserDataField>
            <UserDataLabel>User Type:</UserDataLabel>
            <UserDataValue>{userRole || 'Not provided'}</UserDataValue>
          </UserDataField>
          <UserDataField>
            <UserDataLabel>OLS Student Status:</UserDataLabel>
            <StatusTag aria-label={`OLS student status: ${user.olsStudentStatus || "OLS student prospect"}`}>
              {user.olsStudentStatus || "OLS student prospect"}
            </StatusTag>
          </UserDataField>
          {isEditingProfile ? (
            <Form
              form={profileForm}
              layout="vertical"
              onFinish={handleSaveProfile}
              style={{ width: '100%' }}
            >
              {userData.map((field) => (
                <Form.Item
                  key={field.key}
                  name={field.key}
                  label={<UserDataLabel>{field.label}</UserDataLabel>}
                  rules={[{ required: field.key !== 'credits', message: `Please enter ${field.label.toLowerCase()}` }]}
                >
                  <UserDataInput placeholder={field.label} aria-label={`Input for ${field.label}`} />
                </Form.Item>
              ))}
              <Form.Item>
                <Space>
                  <StyledButton type="primary" htmlType="submit" aria-label="Save profile">
                    Save Profile
                  </StyledButton>
                  <Button onClick={handleCancelEditProfile} aria-label="Cancel profile edit">
                    Cancel
                  </Button>
                </Space>
              </Form.Item>
            </Form>
          ) : (
            <>
              {userData.map((field, index) => (
                <UserDataField key={index}>
                  <UserDataLabel>{field.label}:</UserDataLabel>
                  <UserDataValue>{field.value}</UserDataValue>
                </UserDataField>
              ))}
              <EditButton type="primary" onClick={handleEditProfile} aria-label="Edit profile">
                Edit Profile
              </EditButton>
            </>
          )}
          <Link to="/OLSStudentProspect" aria-label="Join OLS Student Prospect">
            <StyledButton type="primary">Join OLS Student Prospect</StyledButton>
          </Link>
        </SectionCard>
        <SectionCard title="Evaluation Values and Payment">
          <Text style={{ marginBottom: 16, color: '#0427f4' }}>
            Complete your payment to access the OLS Intro page and schedule your session.
          </Text>
          <ValueTable
            columns={valueColumns}
            dataSource={valueData}
            pagination={false}
            rowKey="key"
            aria-label="Evaluation values table"
          />
          <SummaryRow>
            Average Evaluation Value: {averageValue.toFixed(2)}%
          </SummaryRow>
          <TuitionSection>
            <TotalTuition level={3} aria-label={`Total tuition: $${totalTuition.toFixed(2)}`}>
              Total Tuition: ${totalTuition.toFixed(2)}
            </TotalTuition>
            <Title level={4} style={{ color: '#0427f4', marginBottom: 16 }}>
              Select Payment Option
            </Title>
            <CheckboxGroup
              value={[paymentOption]}
              onChange={(values) => setPaymentOption(values[0] || null)}
              aria-label="Payment options"
            >
              <Checkbox
                value="split"
                disabled={introPaymentStatus === 'Completed' && initiPaymentStatus === 'Completed'}
                aria-label="50/50 payment plan"
              >
                50/50 Payment Plan (Intro: ${(totalTuition / 2).toFixed(2)}, Initi: ${(totalTuition / 2).toFixed(2)})
              </Checkbox>
              <Checkbox
                value="full"
                disabled={introPaymentStatus === 'Completed' && initiPaymentStatus === 'Completed'}
                aria-label="100% payment with discount"
              >
                100% Payment with 10% Discount (${(totalTuition * 0.9).toFixed(2)})
              </Checkbox>
            </CheckboxGroup>
            <PaymentButton
              type="primary"
              onClick={() => initiatePayment(paymentOption === 'split' ? 'intro' : 'one')}
              disabled={!paymentOption || (introPaymentStatus === 'Completed' && initiPaymentStatus === 'Completed')}
              loading={paymentLoading}
              aria-label="Make payment"
            >
              Make a Payment
            </PaymentButton>
            <div style={{ marginTop: 16 }}>
              <Text strong>Payment Status:</Text>
              <Text style={{ marginLeft: 8, color: '#0427f4' }} aria-label={`Payment status: Intro ${introPaymentStatus}, Initi ${initiPaymentStatus}`}>
                Intro: {introPaymentStatus}, Initi: {initiPaymentStatus}
              </Text>
            </div>
            {introPaymentStatus === 'Completed' && (
              <SchedulingSection>
                <Title level={4} style={{ color: '#0427f4' }}>
                  Schedule OLS Intro
                </Title>
                <Text style={{ marginBottom: 16, color: '#0427f4' }} aria-label={`Scheduling status: ${introSchedule ? `Scheduled for ${introSchedule}` : 'Not Scheduled'}`}>
                  Scheduling Status: {introSchedule ? `Scheduled for ${introSchedule}` : 'Not Scheduled'}
                </Text>
                <CalendlyWidget
                  className="calendly-inline-widget"
                  data-url="https://calendly.com/olsme/15min?hide_gdpr_banner=1"
                  aria-label="Calendly scheduling widget"
                />
              </SchedulingSection>
            )}
          </TuitionSection>
        </SectionCard>
        <SectionCard title="Evaluation Indicators">
          <Text style={{ marginBottom: 16, color: '#0427f4' }}>
            Waiting for admin evaluation...
          </Text>
          <IndicatorTable
            columns={indicatorsColumns}
            dataSource={indicatorsData}
            pagination={false}
            rowKey="key"
            aria-label="Evaluation indicators table"
          />
          <Title level={4} style={{ color: '#0427f4', marginTop: 24 }}>
            Request New Evaluation
          </Title>
          <Form
            form={evaluationForm}
            layout="vertical"
            onFinish={handleSaveEvaluation}
            style={{ marginTop: 16 }}
          >
            <Form.Item
              name="service"
              label="Evaluation Service"
              rules={[{ required: true, message: 'Please select a service' }]}
            >
              <StyledSelect placeholder="Select a service" aria-label="Select evaluation service">
                {initiServices.map((service) => (
                  <Option key={service} value={service}>
                    {service}
                  </Option>
                ))}
              </StyledSelect>
            </Form.Item>
            <Form.Item>
              <StyledButton type="primary" htmlType="submit" aria-label={editingEvaluation ? 'Update evaluation' : 'Create evaluation'}>
                {editingEvaluation ? 'Update Evaluation' : 'Create Evaluation'}
              </StyledButton>
              {editingEvaluation && (
                <Button
                  style={{ marginLeft: 8 }}
                  onClick={() => { setEditingEvaluation(null); evaluationForm.resetFields(); }}
                  aria-label="Cancel evaluation edit"
                >
                  Cancel Edit
                </Button>
              )}
            </Form.Item>
          </Form>
          {evaluations.length > 0 && (
            <>
              <Title level={4} style={{ color: '#0427f4', marginTop: 24 }}>
                Evaluation List
              </Title>
              <Table
                columns={evaluationColumns}
                dataSource={evaluations}
                pagination={false}
                rowKey="id"
                aria-label="Evaluations list table"
              />
            </>
          )}
        </SectionCard>
        <SectionCard title="Next Steps">
          <NextStepsSection>
            <Text style={{ color: '#0427f4', fontSize: '16px' }} aria-label="Next steps instructions">
              {introPaymentStatus === 'Completed'
                ? 'Proceed to the OLS Introduction page to view your presentation and schedule your session.'
                : 'Complete your OLS Intro payment to unlock the Introduction page and schedule your session.'}
            </Text>
            <Link to="/home/intro" aria-label="Go to OLS Intro page">
              <StyledButton
                type="primary"
                style={{ marginTop: 16 }}
                disabled={introPaymentStatus !== 'Completed'}
                aria-label="Go to OLS Intro"
              >
                Go to OLS Intro
              </StyledButton>
            </Link>
          </NextStepsSection>
        </SectionCard>
      </FlexContainer>
      <StyledModal
        title="Account Type Change"
        open={isModalVisible}
        onOk={onOk}
        onCancel={toggleModal}
        okButtonProps={{ style: { background: '#3552fc', color: '#fff', borderRadius: 8 }, 'aria-label': 'Confirm account type change' }}
        cancelButtonProps={{ style: { borderRadius: 8 }, 'aria-label': 'Cancel account type change' }}
      >
        <Text type="danger">
          Proceeding may overwrite your account data. Contact support for details.
        </Text>
      </StyledModal>
    </EvaluationContainer>
  );
};

export default EvaluationS;